import { forwardRef, useEffect, useState, useRef } from 'react';
import clsx from 'clsx';
import { Help, HelpClose } from '@gjensidige/builders-icons';
import type { TrackingProp } from '../types';
import { useTracking, getLabel, useId, onRoleButtonKeyDown } from '../utils';
import styles from './help-text.module.less';

export type HelpTextProps = React.ComponentPropsWithoutRef<'span'> & {
  onToggle?: React.MouseEventHandler<HTMLSpanElement>;
  toggle?: boolean;
  tracking?: TrackingProp;
};

// Needed when dynamically adding ID to parent label/legend
let UUID = 0;

export const HelpText = forwardRef<HTMLSpanElement, HelpTextProps>(
  function HelpText(
    { className, children, onToggle, toggle, tracking = true, ...rest },
    ref
  ) {
    const btnRef = useRef<HTMLSpanElement>(null);
    const [isOpen, setOpen] = useState(false);
    const track = useTracking(tracking);
    const textId = useId();

    // Setup aria-label on toggle button
    useEffect(() => {
      const label = btnRef.current?.closest('label,legend');
      if (label) {
        label.id = label.id || `builders-helptext-label-${++UUID}`;
        btnRef.current?.setAttribute('aria-describedby', label.id);
      }
    }, [toggle]);

    return (
      <span className={clsx(styles.helpText, className)} ref={ref} {...rest}>
        {toggle && (
          <span
            ref={btnRef}
            aria-controls={textId}
            aria-expanded={isOpen}
            onClick={(event) => {
              event.preventDefault(); // Keep focus on label
              setOpen(!isOpen);
              if (!isOpen)
                track({
                  eventCategory: 'helptext',
                  eventAction: 'open',
                  eventLabel: `helptext_${getLabel(
                    event.currentTarget.closest('label')
                  )}`
                });
              onToggle?.(event);
            }}
            onKeyDown={onRoleButtonKeyDown}
            className={styles.toggle}
            role="button" // Use span with role="button" instead of <button> to enable wrapping in <label>
            tabIndex={0}
          >
            {isOpen ? <HelpClose /> : <Help />}
          </span>
        )}
        <span id={textId} className={styles.text} hidden={toggle && !isOpen}>
          {children}
        </span>
      </span>
    );
  }
);
